import { config } from "../config";
import { NewsRoutePaths } from "@/src/config/router";

type IHomeType = "crypto" | "stock";
type IGetHomeJsonLdProps = {
  type?: IHomeType;
  faq: any[];
};
type IGetNewsJsonLdProps = {
  type?: IHomeType;
  title: string;
  description?: string;
  image?: string[];
  datePublished?: string;
  dateModified?: string;
  author?: any[];
  category?: string;
  slug?: string;
  locale?: string;
};
type NewsThemePath = {
  parentSlug: string;
  slug: string;
};

const stripMarkdown = (md: string, maxLength = 200): string => {
  if (!md) {
    return "";
  }
  const plain = md
    .replace(/^#{1,6}\s+/gm, "") // headings
    .replace(/\*\*(.+?)\*\*/g, "$1") // bold
    .replace(/\*(.+?)\*/g, "$1") // italic
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1") // links/images
    .replace(/`{1,3}[^`]*`{1,3}/g, "") // code
    .replace(/^>\s?/gm, "") // blockquotes
    .replace(/^[-*+]\s+/gm, "") // unordered lists
    .replace(/^\d+\.\s+/gm, "") // ordered lists
    .replace(/\n{2,}/g, " ") // multiple newlines
    .replace(/\n/g, " ") // single newlines
    .trim();
  return plain.length > maxLength ? plain.slice(0, maxLength) + "..." : plain;
};
export const getHomeJsonLd = ({ type, faq }: IGetHomeJsonLdProps) => {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    name: "Edgen AI FAQ",
    about: {
      "@type": "Thing",
      name:
        type === "crypto"
          ? "Crypto Trading AI Platform"
          : "Stock Trading AI Platform",
    },
    author: {
      "@type": "Organization",
      name: "Edgen",
    },
    mainEntity: faq?.map((faq) => ({
      "@type": "Question",
      name: faq?.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq?.answer,
        name: faq?.question, // 使用自定义name
      },
    })),
  };
  return faqJsonLd;
};
export const getNewsJsonLd = ({
  type,
  title,
  description,
  image,
  datePublished,
  dateModified,
  author,
  category,
  slug,
  locale,
}: IGetNewsJsonLdProps) => {
  const baseUrl = `${config.externalLink?.aiChat || ""}${locale !== "en" ? "/" + locale : ""}`;
  const pageUrl =
    category && slug
      ? `${baseUrl}/news/${category}/${slug}`
      : `${baseUrl}/news/${type === "stock" ? "us-stocks" : "crypto"}`;

  const newsJsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: title,
    description: stripMarkdown(description || ""),
    image: image,
    datePublished: datePublished
      ? new Date(datePublished).toISOString()
      : new Date().toISOString(),
    dateModified: dateModified
      ? new Date(dateModified).toISOString()
      : new Date().toISOString(),
    author: author,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": pageUrl,
    },
  };
  return newsJsonLd;
};

export const getNewsDetailBreadcrumbList = ({
  name,
  themePath,
  locale,
  title,
}: {
  name: string;
  locale: string;
  themePath: NewsThemePath;
  title: string;
}) => {
  const categoryPath = themePath
    ? NewsRoutePaths.subTag(themePath.parentSlug, themePath.slug)
    : ``;
  const baseUrl = `${config.externalLink?.aiChat || ""}${locale !== "en" ? "/" + locale : ""}`;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    name,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Edgen",
        item: baseUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: themePath?.parentSlug || "News Category",
        item: `${baseUrl}${categoryPath}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: themePath?.slug || "News Subcategory",
        item: `${baseUrl}/news/${title}`,
      },
    ],
  };
};

/**
 * 生成详情页面的 FAQ JSON-LD 结构化数据
 *
 * 重构说明：
 * - 修复原有的 answer 字段缺失问题，确保 Google 验证通过
 * - 优先使用格式化后的 answer 字段，回退到 messages 提取
 * - 添加数据验证，确保生成的 JSON-LD 包含完整的答案内容
 *
 * @param faq FAQ 数据列表（支持格式化后的数据）
 * @param name Token 名称
 * @returns 符合 Google 要求的 JSON-LD 数据
 */
export const getDetailJsonLd = ({
  faq,
  name,
}: IGetHomeJsonLdProps & { name: string }) => {
  const mainEntity = faq
    ?.map((faq, _index) => {
      // 优先使用格式化后的 answer 字段
      let answerText = faq?.answer;

      // 如果没有 answer 字段，从 messages 中提取
      if (!answerText && faq?.messages) {
        const answerMessage = faq?.messages?.find(
          (item: any) => item.role === "assistant",
        );
        answerText = answerMessage?.content || "";
      }

      const result = {
        "@type": "Question",
        name: faq?.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: answerText || "", // 确保始终有 text 字段，避免 Google 验证失败
          name: faq?.question,
        },
      };

      return result;
    })
    .filter((item) => {
      // 过滤掉没有有效答案的 FAQ
      return (
        item.acceptedAnswer.text && item.acceptedAnswer.text.trim().length > 0
      );
    });

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    name,
    mainEntity: mainEntity || [],
  };

  return faqJsonLd;
};
export const getCryptoBreadcrumbList = (name: string) => {
  const curName = name || "edgen crypto";
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    name: curName,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Edgen",
        item: "https://www.edwealth.ai",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Crypto",
        item: "https://www.edwealth.ai/market/crypto/crypto-depin",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: curName,
      },
    ],
  };
};
